import { computed, onScopeDispose, ref } from 'vue';
import { useSessionStore } from '@/stores/session';

const PHONE_RE = /^1[3-9]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COUNTDOWN_SECONDS = 60;

/**
 * 登录流程状态机：填账号 → 发验证码 → 填验证码 → 登录。
 * 只处理界面状态与提示，网络细节交给 session store。
 */
export function useLogin(options: { onSuccess: () => void }) {
  const session = useSessionStore();

  const step = ref<'account' | 'code'>('account');
  const account = ref('');
  const code = ref('');
  const sending = ref(false);
  const submitting = ref(false);
  const error = ref('');
  const countdown = ref(0);

  let timer: ReturnType<typeof setInterval> | null = null;

  const codeType = computed<'sms' | 'email'>(() => (account.value.includes('@') ? 'email' : 'sms'));
  const accountValid = computed(() =>
    codeType.value === 'email' ? EMAIL_RE.test(account.value.trim()) : PHONE_RE.test(account.value.trim()),
  );
  const canSend = computed(() => accountValid.value && !sending.value && countdown.value === 0);
  const canSubmit = computed(() => /^\d{4,6}$/.test(code.value.trim()) && !submitting.value);

  function stopTimer() {
    if (!timer) return;
    clearInterval(timer);
    timer = null;
  }

  function startCountdown() {
    countdown.value = COUNTDOWN_SECONDS;
    stopTimer();
    timer = setInterval(() => {
      countdown.value -= 1;
      if (countdown.value <= 0) stopTimer();
    }, 1000);
  }

  function reset() {
    step.value = 'account';
    code.value = '';
    error.value = '';
    stopTimer();
    countdown.value = 0;
  }

  async function sendCode() {
    if (!accountValid.value) {
      error.value = codeType.value === 'email' ? '邮箱格式不正确' : '手机号格式不正确';
      return;
    }
    sending.value = true;
    error.value = '';
    try {
      const result = await session.requestCode(account.value.trim(), codeType.value);
      if (result.code !== 200) throw new Error(result.msg || '发送失败，请稍后重试');
      step.value = 'code';
      startCountdown();
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : '发送失败，请稍后重试';
    } finally {
      sending.value = false;
    }
  }

  async function submit() {
    if (!canSubmit.value) {
      error.value = '请输入 4-6 位验证码';
      return;
    }
    submitting.value = true;
    error.value = '';
    try {
      await session.signIn(account.value.trim(), code.value.trim());
      stopTimer();
      options.onSuccess();
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : '登录失败，请稍后重试';
    } finally {
      submitting.value = false;
    }
  }

  onScopeDispose(stopTimer);

  return {
    step,
    account,
    code,
    sending,
    submitting,
    error,
    countdown,
    codeType,
    accountValid,
    canSend,
    canSubmit,
    sendCode,
    submit,
    reset,
  };
}
